import { makeStyles, MenuItem, Theme } from '@material-ui/core';
import { observer } from 'mobx-react';
import * as React from 'react';
import { publicEnumInfoMap } from '../lifecycle/PublicEnumInfoLifecycle';
import { mg } from '../mg/mg';
import { IMgColumn, MgColumnExtras } from '../mg/table/MgColumn';
import { _AdVariationsEdit } from './internal/_AdVariationsEdit';
import { _BooleanEdit } from './internal/_BooleanEdit';
import { _ColorEdit } from './internal/_ColorEdit';
import { _DateEdit } from './internal/_DateEdit';
import { _DatetimeEdit } from './internal/_DatetimeEdit';
import { _IdsAsStringEdit } from './internal/_IdsAsStringEdit';
import { _IEditProps } from './internal/_IEditProps';
import { _ImageLinkEdit } from './internal/_ImageLinkEdit';
import { _SelectEdit } from './internal/_SelectEdit';
import { _StringEdit } from './internal/_StringEdit';
import { transl } from './transl';

const useStyles = makeStyles((theme: Theme) => ({
	root: {
		margin: theme.spacing(0.5, 0),
	},
	menuItemRoot: {
		'&.MuiListItem-root.Mui-selected': {
			backgroundColor: (theme.palette as any).special.main,
			opacity: 0.95,
		},
		'&.MuiListItem-root.Mui-selected:hover': {
			opacity: 1,
		}
	},
}));

interface IOcEditProps {
	column: IMgColumn
	entity: Record<string, any>
	label?: string
	isRequired?: boolean
	reasonForReadonly?: string | boolean
	errorText?: string | boolean
	extras?: MgColumnExtras,
	variant?: 'standard' | 'outlined' | 'filled'
	placeholder?: string | true
	className?: string
	classes?: Record<string, string>
	showErrors?: boolean
}

export const OcEdit = observer(function OcEdit(props: IOcEditProps) {
	const ownClasses = useStyles();
	let {column, entity, label, errorText, isRequired, reasonForReadonly, extras, className, classes, placeholder, variant, showErrors} = props;

  if(extras){
    var e = extras[column.name.toString()];
    if( e && e.members){
      extras.members=e.members;
    }
  }
	if (extras?.hidden) return null;
	errorText = showErrors===false ? '' : errorText ?? extras?.errorText;
	classes = {...ownClasses, ...classes};
	variant = variant ?? 'standard';
	label = label ?? transl(column.label ?? column.name);
	if (placeholder == true) { placeholder = label; label = ''; }
	const accessors = column.accessors(entity);
	isRequired = isRequired ?? column.isRequired;
	const isReadonly = !!reasonForReadonly;
	className = mg.clsn(isReadonly && 'readonly', classes.root, className);
	const enumInfoElement = !isReadonly && publicEnumInfoMap.get(column.type) || undefined;
	const members = extras?.members ? extras.members
		: enumInfoElement ? enumInfoElement.members.map((e, i) => ({ id: e, text: enumInfoElement.translations[i] }))
			: undefined;
	const error = !!errorText;
	const helperText = typeof errorText === 'string' ? errorText : '';
	const type = column.type;
	const internalProps: _IEditProps = {accessors, entity, type, label, error, isRequired, isReadonly, variant, placeholder, helperText, className, classes, members,
		min: extras?.min, max: extras?.max};
	if (members) {
		const children = members.map(e => <MenuItem className={classes!.menuItemRoot} value={e.id} key={e.id}>{e.text}</MenuItem>);
		switch (type) {
		case 'idsAsString':
			return <_IdsAsStringEdit {...internalProps}>{children}</_IdsAsStringEdit>;
		case 'string':
			return <_SelectEdit {...internalProps}>{children}</_SelectEdit>;
		}
		if (enumInfoElement) return <_SelectEdit {...internalProps}>{children}</_SelectEdit>;
	} else {
		switch (type) {
		case 'bool': //TODO remove
		case 'boolean':
			return <_BooleanEdit {...internalProps}/>;
		case 'datetime':
			return <_DatetimeEdit {...internalProps}/>;
		case 'date':
			return <_DateEdit {...internalProps}/>;
		case 'string':
		case 'number':
		case 'int':
		case 'posInt':
		case 'nonNegInt':
			return <_StringEdit {...internalProps}/>;
		case 'color':
			return <_ColorEdit {...internalProps}/>;
		case 'imageLink':
			return <_ImageLinkEdit {...internalProps}/>;
		case 'variationsBase':
			return <_AdVariationsEdit {...internalProps} multiple={false}/>;
		case 'variationsMore':
			return <_AdVariationsEdit {...internalProps} multiple={true}/>;
		}
	}
	mg.assertTrue(false, 'Unknown type', type, members);
	console.error('Unknown type', type, members);
	return <div style={{color: 'red'}}>{JSON.stringify(props)}</div>;
});
